import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {useIssueState} from '@github-ui/issue-pr-state'
import {Link as RouterLink} from '@github-ui/react-router'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {Link as PrimerLink, StateLabel} from '@primer/react'

import {TEST_IDS} from '../../constants/test-ids'
import {getParentIssueUrl} from '../../utils/urls'
import type {IssueNode} from './header-state-query-types'
import styles from './HeaderState.module.css'

// RELAY-CLEANUP(HeaderMetadata, StickyHeaderChips): widens nullable fields so the still-Relay
// parents' data is assignable. Remove when HeaderMetadata and StickyHeaderChips leave Relay.
type DuplicateOfNode = NonNullable<IssueNode['duplicateOf']>
type DuplicateOfRepository = DuplicateOfNode['repository']

export type HeaderStateFragment = RelayCompat<
  Pick<IssueNode, 'state' | 'stateReason'> & {
    readonly duplicateOf?:
      | (Pick<DuplicateOfNode, 'number' | 'url'> & {
          readonly repository: Pick<DuplicateOfRepository, 'name'> & {
            readonly owner: Pick<DuplicateOfRepository['owner'], 'login'>
          }
        })
      | null
  }
>

type HeaderStateProps = {
  isSticky: boolean // whether it is rendered in the normal header or in the sticky header
  stateData: HeaderStateFragment
  repositoryNameWithOwner?: string
}

export function HeaderState({isSticky, stateData, repositoryNameWithOwner}: HeaderStateProps) {
  const {state, stateReason, duplicateOf} = stateData
  const {stateString, stateStatus} = useIssueState({
    state,
    stateReason,
    options: {longText: !isSticky},
  })

  return (
    <div>
      <StateLabel data-testid={TEST_IDS.headerState} status={stateStatus} className={styles.stateLabel}>
        {stateString}
        {<DuplicateIssueLink duplicateOf={duplicateOf} repositoryNameWithOwner={repositoryNameWithOwner} />}
      </StateLabel>
    </div>
  )
}

export function DuplicateIssueLink({
  duplicateOf,
  repositoryNameWithOwner,
  insideStateLabel = true,
}: {
  duplicateOf?: HeaderStateFragment['duplicateOf']
  repositoryNameWithOwner?: string
  insideStateLabel?: boolean
}) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = getHovercardAttributesForType('issue', {
    repo: duplicateOf?.repository?.name ?? '',
    owner: duplicateOf?.repository?.owner?.login ?? '',
    issueNumber: duplicateOf?.number ?? 0,
  })

  if (!duplicateOf) return null

  const duplicateRepository = duplicateOf.repository
  const duplicateOwner = duplicateRepository?.owner?.login
  const duplicateRepoNameWithOwner =
    duplicateOwner && duplicateRepository?.name ? `${duplicateOwner}/${duplicateRepository.name}` : undefined
  const linkUrl =
    repositoryNameWithOwner && duplicateRepoNameWithOwner
      ? getParentIssueUrl(repositoryNameWithOwner, duplicateRepoNameWithOwner, duplicateOf.number, duplicateOf.url)
      : duplicateOf.url

  const link = (
    <PrimerLink
      as={RouterLink}
      to={linkUrl}
      inline
      {...(duplicateOwner ? hovercardAttrs : {})}
      className={insideStateLabel ? styles.duplicateIssueLink : undefined}
    >
      #{duplicateOf.number}
    </PrimerLink>
  )

  return insideStateLabel ? (
    <>
      {' '}
      of
      {link}
    </>
  ) : (
    <span className={styles.duplicateIssueLinkOutside}>of {link}</span>
  )
}
